import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Input, Select, DatePicker, Space, Tag, Modal, 
  Statistic, Row, Col, message, Spin, Tooltip, Badge, Alert
} from 'antd';
import {
  SearchOutlined, DownloadOutlined, ReloadOutlined, FilterOutlined,
  EyeOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
  MobileOutlined, DesktopOutlined, TabletOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import * as serverconfig from '../../serverconn';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const AuditLogViewer = () => {
  console.log('AuditLogViewer component rendered');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    user: '',
    action: '',
    resource_type: '',
    success: null,
    date_range: null,
    search: ''
  });
  
  // Statistics
  const [statistics, setStatistics] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Modal for viewing log details
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize, filters.action, filters.resource_type, filters.success, filters.search, filters.user, filters.date_range]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Use the old API endpoint that we know works (since statistics work)
      console.log('Using old API endpoint for audit logs...');
      const requestData = {
        page: pagination.current,
        page_size: pagination.pageSize,
        user: filters.user || '',
        action: filters.action || '',
        resource_type: filters.resource_type || '',
        success: filters.success,
        date_from: filters.date_range && filters.date_range[0] ? filters.date_range[0].format('YYYY-MM-DD') : '',
        date_to: filters.date_range && filters.date_range[1] ? filters.date_range[1].format('YYYY-MM-DD') : '',
        search: filters.search || ''
      };

      console.log('Request data:', requestData);
      console.log('Token:', localStorage.getItem('token') ? 'Present' : 'Missing');

      const response = await axios.post(
        serverconfig.backendserverurl + '/customqueries/get_audit_logs',
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('Audit logs response:', response.data);
      
      if (response.data.success) {
        setLogs(response.data.logs || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total_count || 0
        }));
      } else {
        message.error('Failed to fetch audit logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      console.error('Error response:', error.response?.data);
      message.error(`Error fetching audit logs: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const days = filters.date_range && filters.date_range[0] ? 
        moment().diff(moment(filters.date_range[0]), 'days') + 1 : 30;

      console.log('Using old statistics API...');
      const requestData = { days };

      const response = await axios.post(
        serverconfig.backendserverurl + '/customqueries/get_audit_statistics',
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('Statistics response:', response.data);
      
      if (response.data.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      console.error('Statistics error response:', error.response?.data);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const exportLogs = async () => {
    try {
      console.log('Using old API for export...');
      const requestData = {
        user: filters.user || '',
        action: filters.action || '',
        resource_type: filters.resource_type || '',
        success: filters.success,
        date_from: filters.date_range && filters.date_range[0] ? filters.date_range[0].format('YYYY-MM-DD') : '',
        date_to: filters.date_range && filters.date_range[1] ? filters.date_range[1].format('YYYY-MM-DD') : '',
        search: filters.search || ''
      };

      const response = await axios.post(
        serverconfig.backendserverurl + '/customqueries/export_audit_logs',
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Convert CSV data to downloadable file
        const csvContent = response.data.csv_data.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit_logs_${moment().format('YYYY-MM-DD')}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        message.success(`Exported ${response.data.total_exported} audit log entries`);
      } else {
        message.error('Failed to export audit logs');
      }
    } catch (error) {
      console.error('Error exporting logs:', error);
      message.error('Error exporting audit logs');
    }
  };

  const showLogDetails = (log) => {
    setSelectedLog(log);
    setModalVisible(true);
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'green',
      'READ': 'blue',
      'UPDATE': 'orange',
      'DELETE': 'red',
      'LOGIN': 'purple',
      'LOGOUT': 'gray',
      'EXPORT': 'teal',
      'PRINT': 'indigo',
      'SCAN': 'pink'
    };
    return colors[action] || 'gray';
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'Mobile':
        return <MobileOutlined />;
      case 'Desktop':
        return <DesktopOutlined />;
      case 'Tablet':
        return <TabletOutlined />;
      default:
        return <DesktopOutlined />;
    }
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      sorter: true,
    },
    {
      title: 'User',
      dataIndex: 'user_username',
      key: 'user',
      width: 150,
      render: (text, record) => (
        <div>
          <div>{text || 'Anonymous'}</div>
          {record.user_email && (
            <small style={{ color: '#666' }}>{record.user_email}</small>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => (
        <Tag color={getActionColor(action)}>
          {action}
        </Tag>
      ),
    },
    {
      title: 'Resource',
      key: 'resource',
      width: 200,
      render: (_, record) => (
        <div>
          <div><strong>{record.resource_type}</strong></div>
          {record.resource_name && (
            <small style={{ color: '#666' }}>{record.resource_name}</small>
          )}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
    },
    {
      title: 'Device',
      dataIndex: 'device_type',
      key: 'device_type',
      width: 100,
      render: (deviceType) => (
        <Space>
          {getDeviceIcon(deviceType)}
          {deviceType}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'success',
      key: 'success',
      width: 100,
      render: (success, record) => (
        <Space>
          {success ? (
            <CheckCircleOutlined style={{ color: 'green' }} />
          ) : (
            <ExclamationCircleOutlined style={{ color: 'red' }} />
          )}
          <span style={{ color: success ? 'green' : 'red' }}>
            {success ? 'Success' : 'Failed'}
          </span>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showLogDetails(record)}
        >
          View
        </Button>
      ),
    },
  ];

  console.log('AuditLogViewer render - logs:', logs.length, 'loading:', loading, 'pagination:', pagination);
  
  return (
    <div style={{ padding: '24px' }}>
      <Card title="System Audit Logs" style={{ marginBottom: 16 }}>
        {/* Statistics */}
        {!statsLoading && statistics.statistics && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="Total Actions"
                value={statistics.statistics.total_actions}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Successful Actions"
                value={statistics.statistics.successful_actions}
                valueStyle={{ color: 'green' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Failed Actions"
                value={statistics.statistics.failed_actions}
                valueStyle={{ color: 'red' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Unique Users"
                value={statistics.statistics.unique_users}
              />
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Search users, descriptions..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Action"
                value={filters.action}
                onChange={(value) => handleFilterChange('action', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="CREATE">Create</Option>
                <Option value="READ">Read</Option>
                <Option value="UPDATE">Update</Option>
                <Option value="DELETE">Delete</Option>
                <Option value="LOGIN">Login</Option>
                <Option value="LOGOUT">Logout</Option>
                <Option value="EXPORT">Export</Option>
                <Option value="PRINT">Print</Option>
                <Option value="SCAN">Scan</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Resource Type"
                value={filters.resource_type}
                onChange={(value) => handleFilterChange('resource_type', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="Delivery">Delivery</Option>
                <Option value="Agent">Agent</Option>
                <Option value="Facility">Facility</Option>
                <Option value="User">User</Option>
                <Option value="Order">Order</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                value={filters.success}
                onChange={(value) => handleFilterChange('success', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value={true}>Success</Option>
                <Option value={false}>Failed</Option>
              </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                value={filters.date_range}
                onChange={(dates) => handleFilterChange('date_range', dates)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchLogs}
                  loading={loading}
                >
                  Search
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setFilters({
                      user: '',
                      action: '',
                      resource_type: '',
                      success: null,
                      date_range: null,
                      search: ''
                    });
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                >
                  Reset
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={exportLogs}
                >
                  Export CSV
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        {logs.length === 0 && !loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No audit logs found. Try adjusting your filters or check if audit logging is enabled.</p>
            <p>Debug info: Loading: {loading.toString()}, Logs count: {logs.length}, Pagination: {JSON.stringify(pagination)}</p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            rowKey="id"
          />
        )}
      </Card>

      {/* Log Details Modal */}
      <Modal
        title="Audit Log Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedLog && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <strong>Timestamp:</strong> {selectedLog.timestamp}
              </Col>
              <Col span={12}>
                <strong>User:</strong> {selectedLog.user_username || 'Anonymous'}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <strong>Action:</strong> 
                <Tag color={getActionColor(selectedLog.action)} style={{ marginLeft: 8 }}>
                  {selectedLog.action}
                </Tag>
              </Col>
              <Col span={12}>
                <strong>Status:</strong> 
                <span style={{ color: selectedLog.success ? 'green' : 'red', marginLeft: 8 }}>
                  {selectedLog.success ? 'Success' : 'Failed'}
                </span>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <strong>Resource Type:</strong> {selectedLog.resource_type}
              </Col>
              <Col span={12}>
                <strong>Resource Name:</strong> {selectedLog.resource_name || 'N/A'}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <strong>IP Address:</strong> {selectedLog.ip_address}
              </Col>
              <Col span={12}>
                <strong>Device:</strong> 
                <Space style={{ marginLeft: 8 }}>
                  {getDeviceIcon(selectedLog.device_type)}
                  {selectedLog.device_type}
                </Space>
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <strong>Description:</strong>
              <div style={{ marginTop: 8 }}>{selectedLog.description}</div>
            </div>
            {selectedLog.error_message && (
              <div style={{ marginTop: 16 }}>
                <strong>Error Message:</strong>
                <Alert
                  message={selectedLog.error_message}
                  type="error"
                  style={{ marginTop: 8 }}
                />
              </div>
            )}
            {selectedLog.new_values && (
              <div style={{ marginTop: 16 }}>
                <strong>New Values:</strong>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 8, 
                  marginTop: 8,
                  borderRadius: 4,
                  fontSize: 12
                }}>
                  {JSON.stringify(selectedLog.new_values, null, 2)}
                </pre>
              </div>
            )}
            {selectedLog.old_values && (
              <div style={{ marginTop: 16 }}>
                <strong>Old Values:</strong>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 8, 
                  marginTop: 8,
                  borderRadius: 4,
                  fontSize: 12
                }}>
                  {JSON.stringify(selectedLog.old_values, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogViewer;
